import { computed, DestroyRef, inject, Injectable, linkedSignal, NgZone, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { MatSnackBar } from '@angular/material/snack-bar';

import { finalize, map, switchMap, take, tap } from 'rxjs/operators';
import { from, Observable } from 'rxjs';

import * as _ from 'lodash';

import { BaseMultipleWidget } from '@website-builder/widgets/base/base-multiple-widget';
import { BaseWidget } from '@website-builder/widgets/base/base-widget';
import { BuilderElementPosition } from '@website-builder/model/structure/builder-element-position';
import { BuilderElementType } from '@website-builder/model/domain/builder-element-type.enum';
import { BuilderMainPart } from '@website-builder/model/structure/builder-main-part.type';
import { BuilderRootStructureType } from '@website-builder/model/state/builder-root-structure.type';
import { BuilderSamples } from '@landing-page/application/builder-samples';
import { BuilderTemplate } from '@website-builder/model/template/builder-template.type';
import { collectUsedImages } from '@website-builder/application/media/collect-used-images';
import { Column } from '@website-builder/model/structure/column/column';
import { ColumnLayout } from '@website-builder/model/structure/column/column-layout';
import { DialogService } from '@core/dialog/shared/services/dialog.service';
import { EntityIdPrefix } from '@website-builder/model/domain/entity-id-prefix.enum';
import { Footer } from '@website-builder/model/structure/footer';
import { GlobalButtonStyle } from '@website-builder/model/style/global-button-style';
import { Header } from '@website-builder/model/structure/header/header';
import { HeaderSection } from '@website-builder/model/structure/section/header-section';
import { IframeContextService } from '@website-builder/application/iframe-context.service';
import { ImageService } from '@core/services/image.service';
import { LayoutRegistry } from '@website-builder/model/structure/layout-registry/layout-registry';
import { makeEntityId } from '@website-builder/util/make-widget-id.utility';
import { MenuElement } from '@website-builder/widgets/menu/menu-element';
import { MenuWidget } from '@website-builder/widgets/menu/menu-widget';
import { NewPageResponse } from '@website-builder/model/structure/new-page-response';
import { NewWidgetComponent } from '@website-builder/dialogs/new-widget/new-widget.component';
import { Page } from '@website-builder/model/structure/page';
import { PagePreview } from '@domains/template/model/api/page-preview.interface';
import { PageReferenceUrl } from '@website-builder/model/domain/link/page-reference-url';
import { Row } from '@website-builder/model/structure/row/row';
import { Section } from '@website-builder/model/structure/section/section';
import { StyleBreakPoint } from '@website-builder/model/structure/breakpoint.enum';
import { TableWidget } from '@website-builder/widgets/table/table-widget';
import { TbLink } from '@website-builder/model/domain/link/tb-link';
import { TbUtility } from '@website-builder/util/tb-utility';
import { Template } from '@domains/template/model/api/template.type';
import { TemplateApi } from '@domains/template/data-access/template.api';
import { templateCompare } from '@website-builder/application/template/template-compare';
import { TemplateKindType } from '@domains/template/model/api/template-kind-type.enum';
import { toBuilderTemplate } from '@website-builder/application/template/to-builder-template';
import { ToTopWidget } from '@website-builder/widgets/to-top/to-top-widget';
import { UpdateTemplatesRequest } from '@domains/template/model/api/update-template-request.interface';
import { Website } from '@website-builder/model/domain/website/website';
import { WebsiteColor } from '@website-builder/model/domain/website-color';
import { WebsiteConfiguration } from '@website-builder/model/domain/website/website-configuration';
import { WebsiteFont } from '@website-builder/model/domain/website-font';
import { WebsiteTemplate } from '@domains/template/model/api/website-template.type';
import { WebsiteTemplateNormalizer } from '@website-builder/application/template/website-template-normalizer';

@Injectable({ providedIn: 'root' })
export class BuilderTemplateService {
  readonly #zone = inject(NgZone);
  readonly #snackBar = inject(MatSnackBar);
  readonly #destroyRef = inject(DestroyRef);
  readonly #templateApi = inject(TemplateApi);
  readonly #imageService = inject(ImageService);
  readonly #dialogService = inject(DialogService);
  readonly #iframeContextService = inject(IframeContextService);

  #globalButtonStyleProperties = new GlobalButtonStyle();

  readonly #document = computed<Document>(() => {
    const iframeDocument = this.#iframeContextService.document();

    if (!iframeDocument) {
      return document;
    }

    return iframeDocument;
  });

  readonly #loading = signal<boolean>(true);

  readonly loading = this.#loading.asReadonly();

  readonly #originalWebsiteTemplateState = signal<WebsiteTemplate | null>(null);
  readonly #draftWebsiteTemplateState = signal<BuilderTemplate<TemplateKindType.WEBSITE> | null>(null);

  readonly #globalButtonStyles = linkedSignal<GlobalButtonStyle | null>(() => {
    const builderTemplate = this.#draftWebsiteTemplateState();

    if (!builderTemplate) {
      return null;
    }

    const data = builderTemplate.data;

    if (!data.config.buttonGlobalStyles) {
      data.config.buttonGlobalStyles = new GlobalButtonStyle();
    }

    return data.config.buttonGlobalStyles;
  });

  readonly headerTemplateList = signal<BuilderTemplate<TemplateKindType.HEADER>[] | null>(null);
  readonly pageBodyTemplateList = signal<BuilderTemplate<TemplateKindType.PAGE>[] | null>(null);
  readonly footerTemplateList = signal<BuilderTemplate<TemplateKindType.FOOTER>[] | null>(null);

  readonly draftWebsiteTemplateState = this.#draftWebsiteTemplateState.asReadonly();
  readonly globalButtonStyles = this.#globalButtonStyles.asReadonly();

  static windowHeight: number;

  readonly configFonts = computed<WebsiteFont[] | null>(() => {
    const template = this.draftWebsiteTemplateState();

    if (!template) {
      return null;
    }

    return template.data.config.fonts;
  });
  readonly configColors = computed<WebsiteColor[] | null>(() => {
    const template = this.draftWebsiteTemplateState();

    if (!template) {
      return null;
    }

    return template.data.config.colors;
  });
  readonly isTemplateDirty = computed<boolean>(() => {
    const original = this.#originalWebsiteTemplateState();
    const draft = this.#draftWebsiteTemplateState();

    if (!original || !draft) {
      return false;
    }

    const originalObj = this.#safeParse(original.data);

    return templateCompare(originalObj, draft.data);
  });

  constructor() {
    this.#loadHeaders();
    this.#loadDefaultPages();
    this.#loadFooters();
  }

  loadWebsiteTemplateById(id: number): Observable<BuilderTemplate<TemplateKindType.WEBSITE>> {
    this.#loading.set(true);

    return this.#templateApi.templateById<WebsiteTemplate>(id).pipe(
      map(pureTemplate => {
        return toBuilderTemplate<TemplateKindType.WEBSITE>(pureTemplate);
      }),
      tap(websiteTemplate => {
        WebsiteTemplateNormalizer.normalizeInPlace(websiteTemplate.data);
      }),
      tap(pureTemplate => {
        this.#originalWebsiteTemplateState.set({ ...pureTemplate, data: JSON.stringify(pureTemplate.data) });
      }),
      tap(websiteTemplate => {
        this.#draftWebsiteTemplateState.set(websiteTemplate);
      }),
      finalize(() => this.#loading.set(false)),
    );
  }

  addHeaderDraft(header: Header): void {
    this.#draftWebsiteTemplateState.update(draft => {
      if (!draft.data.headers) {
        draft.data.headers = [];
      }

      return {
        ...draft,
        data: {
          ...draft.data,
          headers: [header, ...draft.data.headers],
        },
      };
    });
  }

  addFooterDraft(footer: Footer): void {
    this.#draftWebsiteTemplateState.update(draft => {
      if (!draft.data.footers) {
        draft.data.footers = [];
      }

      return {
        ...draft,
        data: {
          ...draft.data,
          footers: [footer, ...draft.data.footers],
        },
      };
    });
  }

  removeHeaderDraft(headerId: number): void {
    this.#draftWebsiteTemplateState.update(draft => {
      if (!draft?.data?.headers) {
        return draft;
      }

      return {
        ...draft,
        data: {
          ...draft.data,
          headers: draft.data.headers.filter(header => header.id !== headerId),
        },
      };
    });
  }

  removeFooterDraft(footerId: number): void {
    this.#draftWebsiteTemplateState.update(draft => {
      if (!draft?.data?.footers) {
        return draft;
      }

      return {
        ...draft,
        data: {
          ...draft.data,
          footers: draft.data.footers.filter(footer => footer.id !== footerId),
        },
      };
    });
  }

  setCustomWebsiteTemplate(template: BuilderTemplate<TemplateKindType.WEBSITE>): void {
    this.#originalWebsiteTemplateState.set(null);

    const draft: BuilderTemplate<TemplateKindType.WEBSITE> = {
      ...template,
      data: template.data,
    };

    this.#draftWebsiteTemplateState.set(draft);
  }

  patchDraft(patch: Partial<BuilderTemplate<TemplateKindType.WEBSITE>>): void {
    this.#draftWebsiteTemplateState.update(draft => (draft ? { ...draft, ...patch } : draft));
  }

  resetDraftTemplateState(): void {
    const originalState = this.#originalWebsiteTemplateState();
    if (!originalState) {
      return;
    }

    this.#draftWebsiteTemplateState.set({ ...originalState, data: this.#safeParse(originalState.data) });
  }

  saveTemplate(): Observable<UpdateTemplatesRequest[]> {
    const original = this.#originalWebsiteTemplateState();
    const draft = this.#draftWebsiteTemplateState();
    const imagesMap = this.#imageService.imagesById();

    if (!original || !draft) {
      return;
    }

    const { usedIds } = collectUsedImages(draft.data, imagesMap);

    const payload: UpdateTemplatesRequest = {
      ...draft,
      categories: original.categories.map(category => category.id),
      id: original.id,
      data: JSON.stringify(draft.data),
      pagePreviews: draft.data.pages.length
        ? this.#getPagePreviewList(draft, draft.data.pages)
        : [BuilderSamples.pagePreview],
      contentImages: usedIds,
    };

    this.#loading.set(true);

    return this.#templateApi.update([payload]).pipe(
      tap(() => {
        const nextOriginal: Template = {
          ...original,
          ...draft,
          data: JSON.stringify(draft.data ?? null),
        };

        this.#originalWebsiteTemplateState.set(nextOriginal);
        this.resetDraftTemplateState();

        this.#snackBar.open(`The template has been successfully updated.`, undefined, {
          duration: 3000,
          panelClass: ['tess-snackbar', 'tess-snackbar--success'],
        });
      }),
      finalize(() => this.#loading.set(false)),
    );
  }

  patchDraftFonts(fonts: WebsiteFont[]): void {
    this.#draftWebsiteTemplateState.update(draft => {
      return {
        ...draft,
        data: {
          ...draft.data,
          config: {
            ...draft.data.config,
            fonts: [...fonts],
          },
        },
      };
    });
  }

  patchDraftColors(colors: WebsiteColor[]): void {
    this.#draftWebsiteTemplateState.update(draft => {
      return {
        ...draft,
        data: {
          ...draft.data,
          config: {
            ...draft.data.config,
            colors: [...colors],
          },
        },
      };
    });
  }

  patchDraftConfig(websiteConfig: WebsiteConfiguration): void {
    this.#draftWebsiteTemplateState.update(draft => {
      return {
        ...draft,
        data: {
          ...draft.data,
          config: {
            ...websiteConfig,
          },
        },
      };
    });
  }

  draftAddPage(page: Page): void {
    this.#draftWebsiteTemplateState.update(draft => {
      return {
        ...draft,
        data: {
          ...draft.data,
          pages: [...draft.data.pages, page],
        },
      };
    });
  }

  draftPatchPage(index: number, page: Page): void {
    this.#draftWebsiteTemplateState.update(draft => {
      draft.data.pages[index] = { ...page };

      return {
        ...draft,
        data: {
          ...draft.data,
          pages: [...draft.data.pages],
        },
      };
    });
  }

  patchDraftPageById(pageId: string | number, patch: Partial<Page>): Page | null {
    let updatedPage: Page | null = null;

    this.#draftWebsiteTemplateState.update(draft => {
      if (!draft) {
        return draft;
      }

      const index = draft.data.pages.findIndex(page => page.id === pageId);

      if (index === -1) {
        return draft;
      }

      updatedPage = {
        ...draft.data.pages[index],
        ...patch,
      };

      const pages = [...draft.data.pages];
      pages[index] = updatedPage;

      return {
        ...draft,
        data: {
          ...draft.data,
          pages,
        },
      };
    });

    return updatedPage;
  }

  draftDeletePage(index: number): void {
    this.#draftWebsiteTemplateState.update(draft => {
      const pages = draft.data.pages.slice();
      pages.splice(index, 1);

      return {
        ...draft,
        data: {
          ...draft.data,
          pages,
        },
      };
    });
  }

  #safeParse(data: string): Website | null {
    try {
      return data ? JSON.parse(data) : null;
    } catch {
      return null;
    }
  }

  changeGlobalButtonStyle(newGlobalStyle: GlobalButtonStyle): void {
    this.#draftWebsiteTemplateState.update(draft => {
      return {
        ...draft,
        data: {
          ...draft.data,
          config: {
            ...draft.data.config,
            buttonGlobalStyles: { ...newGlobalStyle },
          },
        },
      };
    });
  }

  updateToTop(toTop: ToTopWidget): void {
    const website = this.draftWebsiteTemplateState()?.data;

    if (!website) {
      return;
    }

    this.#draftWebsiteTemplateState.update(draft => {
      return {
        ...draft,
        data: {
          ...draft.data,
          config: {
            ...draft.data.config,
            toTop: { ...toTop },
          },
        },
      };
    });
  }

  createRow(layout: number[] = [1]): Row {
    const row = new Row();

    row.originalLayout = [...layout];
    row.columns = layout.map((_, index) => {
      const column = new Column();
      column.layout = this.#createColumnLayout(layout, index);

      return column;
    });

    return row;
  }

  createSection(type: BuilderRootStructureType, rowLayout: number[] = [1]): Section {
    const section = type === BuilderElementType.Header ? new HeaderSection() : new Section();
    const row = this.createRow(rowLayout);
    section.rows.push(row);

    return section;
  }

  addWidgetDialog(type: BuilderElementType): Observable<BaseWidget | undefined> {
    return from(
      import(
        /* webpackPrefetch: true */
        '@website-builder/dialogs/new-widget/new-widget.component'
      ),
    ).pipe(
      // eslint-disable-next-line @typescript-eslint/naming-convention
      switchMap(({ NewWidgetComponent }) =>
        this.#dialogService
          .open<NewWidgetComponent, BaseWidget | undefined>(NewWidgetComponent, {
            title: 'New widget',
            style: 'new-widget-dialog',
            data: { isHeader: type === 'Header' },
          })
          .afterClosed$(),
      ),
    );
  }

  editWidget(widget: BaseWidget): void {
    widget.style.outline = '';
    this.#zone.runOutsideAngular(() => BuilderTemplateService.bringWidgetInView(widget, this.#document()));
  }

  duplicateWidget(column: Column, widIdx: number): BaseWidget {
    const newWidget = _.cloneDeep(column.widgets[widIdx]);
    newWidget.id = makeEntityId(EntityIdPrefix.WIDGET);
    newWidget.style.outline = '';

    return newWidget;
  }

  updateWidgetsLink(previousLink: string, currentLink: string | TbLink): void {
    const templateState = this.draftWebsiteTemplateState();

    if (!templateState) {
      return;
    }

    const replacementCallback = (item: unknown): void => {
      PageReferenceUrl.replacePageReference(item, previousLink, currentLink);
    };

    templateState.data.pages.forEach(page =>
      BuilderTemplateService.replaceLinkInEveryWidgetOfWebsitePart(page, replacementCallback),
    );
    (templateState.data.headers ?? []).forEach(header =>
      BuilderTemplateService.replaceLinkInEveryWidgetOfWebsitePart(header, replacementCallback),
    );
    (templateState.data.footers ?? []).forEach(footer =>
      BuilderTemplateService.replaceLinkInEveryWidgetOfWebsitePart(footer, replacementCallback),
    );
  }

  updateWebsiteStructure(pageStructure: NewPageResponse): void {
    const template = this.draftWebsiteTemplateState();

    if (!template) {
      return;
    }

    const data = template.data;

    const idx = data.pages.findIndex(p => p.id === pageStructure.page.id);
    const id = data.pages[idx].id;

    this.#draftWebsiteTemplateState.update(draftTemplateState => {
      draftTemplateState.data.pages[idx] = { ...pageStructure.page };
      draftTemplateState.data.pages[idx].id = id;

      return { ...draftTemplateState, data: { ...draftTemplateState.data, pages: [...draftTemplateState.data.pages] } };
    });
  }

  get globalButtonStyle(): GlobalButtonStyle {
    return this.#globalButtonStyleProperties;
  }

  patchDraftWebsiteTemplateHeader(updatedHeader: Header): void {
    const draftWebsiteTemplateState = this.#draftWebsiteTemplateState();

    if (!draftWebsiteTemplateState || !draftWebsiteTemplateState.data || !draftWebsiteTemplateState.data.headers) {
      return;
    }

    const index = draftWebsiteTemplateState.data.headers.findIndex(header => header.id === updatedHeader.id);

    if (index === -1) {
      return;
    }

    this.#draftWebsiteTemplateState.update(draftWebsiteTemplateState => {
      draftWebsiteTemplateState.data.headers[index] = { ...updatedHeader };

      return {
        ...draftWebsiteTemplateState,
        data: {
          ...draftWebsiteTemplateState.data,
        },
      };
    });
  }

  patchDraftWebsiteTemplateFooter(updatedFooter: Footer): void {
    const draftWebsiteTemplateState = this.#draftWebsiteTemplateState();

    if (!draftWebsiteTemplateState || !draftWebsiteTemplateState.data || !draftWebsiteTemplateState.data.footers) {
      return;
    }

    const index = draftWebsiteTemplateState.data.footers.findIndex(footer => footer.id === updatedFooter.id);

    if (index === -1) {
      return;
    }

    this.#draftWebsiteTemplateState.update(draftWebsiteTemplateState => {
      draftWebsiteTemplateState.data.footers[index] = { ...updatedFooter };

      return {
        ...draftWebsiteTemplateState,
        data: {
          ...draftWebsiteTemplateState.data,
        },
      };
    });
  }

  addWidget(componentPosition: BuilderElementPosition, pageData: BuilderMainPart): void {
    const template = this.draftWebsiteTemplateState();

    if (!template) {
      return;
    }

    const data = template.data;

    pageData.sections.forEach((section, sectionIndex) => {
      if (componentPosition.sectionIndex === sectionIndex) {
        section.rows.forEach((row, rowIndex) => {
          if (componentPosition.rowIndex === rowIndex) {
            row.columns.forEach((column, columnIndex) => {
              if (componentPosition.columnIndex === columnIndex) {
                column.widgets.splice(
                  componentPosition.widgetIndex + 1,
                  0,
                  componentPosition.updatedElement as BaseWidget,
                );
              }
            });
          }
        });
      }
    });

    this.#recompilePageData(pageData, data);
  }

  removeWidget(componentPosition: BuilderElementPosition, pageData: Page | Header | Footer): void {
    const template = this.draftWebsiteTemplateState();

    if (!template) {
      return;
    }

    const data = template.data;

    pageData.sections.forEach((section, sectionIndex) => {
      if (componentPosition.sectionIndex === sectionIndex) {
        section.rows.forEach((row, rowIndex) => {
          if (componentPosition.rowIndex === rowIndex) {
            row.columns.forEach((column, columnIndex) => {
              if (componentPosition.columnIndex === columnIndex) {
                column.widgets.splice(componentPosition.widgetIndex, 1);
              }
            });
          }
        });
      }
    });

    this.#recompilePageData(pageData, data);
  }

  addRow(componentPosition: BuilderElementPosition, pageData: Page | Footer | Header): void {
    const template = this.draftWebsiteTemplateState();

    if (!template) {
      return;
    }

    const data = template.data;

    pageData.sections.forEach((section, sectionIndex) => {
      if (componentPosition.sectionIndex === sectionIndex) {
        section.rows.splice(componentPosition.rowIndex, 0, componentPosition.updatedElement as Row);
      }
    });

    this.#recompilePageData(pageData, data);
  }

  removeRow(componentPosition: BuilderElementPosition, pageData: Page | Footer | Header): void {
    const template = this.draftWebsiteTemplateState();

    if (!template) {
      return;
    }

    pageData.sections.forEach((section, sectionIndex) => {
      if (componentPosition.sectionIndex === sectionIndex) {
        if (section.rows.length) {
          section.rows.splice(componentPosition.rowIndex, 1);

          if (!section.rows.length) {
            const newRow = new Row();
            newRow.originalLayout = [1];
            newRow.columns.push(new Column());

            section.rows.push(newRow);
          }
        }
      }
    });

    this.#recompilePageData(pageData, template.data);
  }

  updateBuilderElementData(
    updated: Section | Row | Column | BaseWidget,
    componentPosition: BuilderElementPosition,
    pageData: Page | Footer | Header,
  ): void {
    const template = this.draftWebsiteTemplateState();

    if (!template) {
      return;
    }

    const website = template.data;

    const sections = [...pageData.sections];
    const section = { ...sections[componentPosition.sectionIndex] };

    if (componentPosition.rowIndex === undefined) {
      sections[componentPosition.sectionIndex] = { ...(updated as Section) };
    } else {
      const rows = [...section.rows];
      const row = { ...rows[componentPosition.rowIndex] };

      if (componentPosition.columnIndex === undefined) {
        rows[componentPosition.rowIndex] = { ...(updated as Row) };
        section.rows = rows;
      } else {
        const columns = [...row.columns];
        const column = { ...columns[componentPosition.columnIndex] };

        if (componentPosition.widgetIndex === undefined) {
          columns[componentPosition.columnIndex] = { ...(updated as Column) };
          row.columns = columns;
          rows[componentPosition.rowIndex] = row;
          section.rows = rows;
        } else {
          const widgets = [...column.widgets];
          const index = widgets.findIndex(widget => widget.id === updated.id);
          widgets[index === -1 ? componentPosition.widgetIndex : index] = { ...(updated as BaseWidget) };
          column.widgets = widgets;
          columns[componentPosition.columnIndex] = column;
          row.columns = columns;
          rows[componentPosition.rowIndex] = row;
          section.rows = rows;
        }
      }

      sections[componentPosition.sectionIndex] = section;
    }

    this.#recompilePageData(pageData, website);
  }

  addSection(componentPosition: BuilderElementPosition, pageData: Page | Footer | Header): void {
    const template = this.draftWebsiteTemplateState();

    if (!template) {
      return;
    }

    pageData.sections.splice(componentPosition.sectionIndex, 0, componentPosition.updatedElement as Section);

    this.#recompilePageData(pageData, template.data);
  }

  removeSection(componentPosition: BuilderElementPosition, pageData: Page | Footer | Header): void {
    const template = this.draftWebsiteTemplateState();

    if (!template) {
      return;
    }

    pageData.sections.splice(componentPosition.sectionIndex, 1);

    if (!pageData.sections.length) {
      pageData.sections.push(componentPosition.updatedElement as HeaderSection);
    }

    this.#recompilePageData(pageData, template.data);
  }

  getWidgetsWhichReferencePage(link: string): Map<BuilderMainPart, BaseWidget[]> {
    const map = new Map<BuilderMainPart, BaseWidget[]>();
    const templateState = this.draftWebsiteTemplateState();

    if (!templateState) {
      return map;
    }

    const getWidgetsWithReferenceForWebsitePart = (websiteParts: BuilderMainPart[]): void => {
      websiteParts.forEach(part => {
        const widgetsFromWebsitePart = BuilderTemplateService.getWidgetsFromWebsitePart(link, part);
        if (widgetsFromWebsitePart.length > 0) {
          map.set(part, widgetsFromWebsitePart);
        }
      });
    };

    getWidgetsWithReferenceForWebsitePart(templateState.data.pages);
    getWidgetsWithReferenceForWebsitePart(templateState.data.headers ?? []);
    getWidgetsWithReferenceForWebsitePart(templateState.data.footers ?? []);

    return map;
  }

  #loadHeaders(): void {
    this.#templateApi
      .getAll({
        kinds: [TemplateKindType.HEADER],
      })
      .pipe(
        map(pageTemplates => {
          return pageTemplates.items.map(pageTemplate => {
            return toBuilderTemplate<TemplateKindType.HEADER>(pageTemplate);
          });
        }),
        tap(headers => {
          headers.forEach(header => {
            Header.normalizeInPlace(header.data);
          });
        }),
        take(1),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe({
        next: headers => {
          this.headerTemplateList.set(headers);
        },
      });
  }

  #loadDefaultPages(): void {
    this.#templateApi
      .getAll({
        kinds: [TemplateKindType.PAGE],
        showOwned: false,
        showPublic: true,
      })
      .pipe(
        map(pageData => {
          return pageData.items.map(pageTemplate => {
            return toBuilderTemplate<TemplateKindType.PAGE>(pageTemplate);
          });
        }),
        tap(pages => {
          pages.forEach(header => {
            Page.normalizeInPlace(header.data);
          });
        }),
        take(1),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe({
        next: defaultPages => {
          this.pageBodyTemplateList.set(defaultPages);
        },
      });
  }

  #loadFooters(): void {
    this.#templateApi
      .getAll({
        kinds: [TemplateKindType.FOOTER],
      })
      .pipe(
        map(pageTemplates => {
          return pageTemplates.items.map(pageTemplate => {
            return toBuilderTemplate<TemplateKindType.FOOTER>(pageTemplate);
          });
        }),
        tap(footers => {
          footers.forEach(footer => {
            Footer.normalizeInPlace(footer.data);
          });
        }),
        take(1),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe({
        next: pageTemplates => {
          this.footerTemplateList.set(pageTemplates);
        },
      });
  }

  #recompilePageData(pageData: BuilderMainPart, website: Website): void {
    if (pageData.type === BuilderElementType.Page) {
      const pages = website.pages.map(page => (page.id === pageData.id ? { ...pageData } : page)) as Page[];

      this.#draftWebsiteTemplateState.update(draft => {
        return { ...draft, data: { ...website, pages } };
      });
    } else if (pageData.type === BuilderElementType.Header) {
      this.patchDraftWebsiteTemplateHeader({ ...pageData } as Header);
    } else if (pageData.type === BuilderElementType.Footer) {
      this.patchDraftWebsiteTemplateFooter({ ...pageData } as Footer);
    }
  }

  #getPagePreviewList(builderTemplate: BuilderTemplate<TemplateKindType.WEBSITE>, pages: Page[]): PagePreview[] {
    const pagePreviewList: PagePreview[] = [];

    for (let i = 0; i < pages.length; i++) {
      pagePreviewList.push({
        id: TbUtility.randomIdAsNumber,
        name: pages[i].title,
        description: pages[i].title,
        languageCode: pages[i].languageCode,
        disabled: pages[i].disabled,
        imagePreview: builderTemplate.imagePreview,
        imagePreviewKey: builderTemplate.imagePreviewKey,
        imagePreviewUrl: builderTemplate.imagePreviewUrl,
        imagePreviewThumbnailUrl: builderTemplate.imagePreviewThumbnailUrl,
        title: pages[i].title,
        order: i,
      });
    }

    return pagePreviewList;
  }

  #createColumnLayout(layout: number[], columnIndex: number): ColumnLayout {
    const layoutRegistryState = LayoutRegistry.getWebsiteLayout(layout);
    const columnLayout = new ColumnLayout();
    const classStyle = layoutRegistryState.classes?.[columnIndex] ?? '';
    const classStyleList = classStyle.split(' ');

    classStyleList.forEach(classStyleElement => {
      if (!classStyleElement) {
        return;
      }

      let breakpoint: StyleBreakPoint | undefined = classStyleElement.split('@')[1] as StyleBreakPoint;

      if (!breakpoint) {
        columnLayout.phonePortrait = classStyleElement;
        return;
      }

      breakpoint = ('@' + breakpoint) as StyleBreakPoint;

      switch (breakpoint) {
        case StyleBreakPoint.SMALL:
          columnLayout.phoneLandscape = classStyleElement;
          break;
        case StyleBreakPoint.MIDDLE:
          columnLayout.tabletLandscape = classStyleElement;
          break;
        case StyleBreakPoint.LARGE:
          columnLayout.desktop = classStyleElement;
          break;
        case StyleBreakPoint.X_LARGE:
          columnLayout.largeScreen = classStyleElement;
          break;
      }
    });

    return columnLayout;
  }

  static getAllWidgetsFromWebsitePart(part: BuilderMainPart): BaseWidget[] {
    return (
      part.sections?.flatMap(
        (section: Section) =>
          section.rows?.flatMap(row => row.columns?.flatMap(column => column.widgets ?? []) ?? []) ?? [],
      ) ?? []
    );
  }

  static bringWidgetInView(widget: BaseWidget, document: Document): void {
    const box = document?.getElementById(widget.id).getBoundingClientRect();
    this.windowHeight = window.innerHeight;

    const viewportDesiredOffset = this.getOffsetForMovingTheWidget(box);

    const elementPosition = box.top + window.pageYOffset;
    const windowPositionAfterScroll = elementPosition - viewportDesiredOffset;

    if (!BuilderTemplateService.isEntireWidgetVisible(box)) {
      window.scrollTo({
        top: windowPositionAfterScroll,
        behavior: 'smooth',
      });
    }

    const distanceToWidget = Math.abs(window.pageYOffset - windowPositionAfterScroll);
    let flashWidgetDelay = 800;

    if (distanceToWidget <= 500) {
      flashWidgetDelay = distanceToWidget;
    }

    setTimeout(() => {
      document?.getElementById(widget.id).classList.add('flash-effect');
    }, flashWidgetDelay);

    setTimeout(() => {
      document?.getElementById(widget.id).classList.remove('flash-effect');
    }, flashWidgetDelay + 1500);
  }

  static isEntireWidgetVisible(box): boolean {
    const boxTop = box.top;
    const boxBottom = box.bottom;
    return boxTop < this.windowHeight && boxBottom > 0 && boxTop > 0 && boxBottom < this.windowHeight;
  }

  static replaceLinkInEveryWidgetOfWebsitePart(part: BuilderMainPart, callback: (item: unknown) => void): void {
    const allWidgetsFromWebsitePart = BuilderTemplateService.getAllWidgetsFromWebsitePart(part);
    allWidgetsFromWebsitePart.forEach(widget => {
      if (widget instanceof MenuWidget) {
        BuilderTemplateService.replaceLinkInMenu(widget, callback);
      } else if (widget instanceof BaseMultipleWidget) {
        BuilderTemplateService.replaceLinkInMultipleWidget(widget, callback);
      } else {
        callback(widget);
      }
    });
  }

  static replaceLinkInMultipleWidget(widget: BaseMultipleWidget<unknown>, callback: (item: unknown) => void): void {
    if (widget instanceof TableWidget) {
      widget.elements.forEach(element =>
        element.cells.filter(cell => cell.type === 'button').forEach(cell => callback(cell)),
      );
    } else {
      widget.elements.forEach(element => callback(element));
    }
  }

  static replaceLinkInMenu(menu: MenuWidget, callback: (item: unknown) => void): void {
    menu.menuSections.forEach(section => BuilderTemplateService.replaceLinkInMenuItemList(section.items, callback));
  }

  static replaceLinkInMenuItemList(menuItems: MenuElement[], callback: (item: unknown) => void): void {
    if (menuItems) {
      menuItems.forEach(item => {
        callback(item);
        BuilderTemplateService.replaceLinkInMenuItemList(item.items, callback);
      });
    }
  }

  static getWidgetsFromWebsitePart(link: string, part: BuilderMainPart): BaseWidget[] {
    const widgetsWithReference = new Array<BaseWidget>();

    part.sections?.forEach(section =>
      section.rows.forEach(row =>
        row.columns.forEach(column =>
          column.widgets.forEach(widget => {
            let referenceInWidget: boolean;

            if (widget instanceof MenuWidget) {
              referenceInWidget = BuilderTemplateService.isPageReferenceInMenu(widget, link);
            } else if (widget instanceof BaseMultipleWidget) {
              referenceInWidget = BuilderTemplateService.isPageReferenceInMultipleWidgetType(widget, link);
            } else {
              referenceInWidget = BuilderTemplateService.hasLink(widget, link);
            }

            if (referenceInWidget) {
              widgetsWithReference.push(widget);
            }
          }),
        ),
      ),
    );

    return widgetsWithReference;
  }

  static isPageReferenceInMultipleWidgetType(widget: BaseMultipleWidget<unknown>, link: string): boolean {
    if (widget instanceof TableWidget) {
      return widget.elements.some(element =>
        element.cells.filter(cell => cell.type === 'button').some(cell => BuilderTemplateService.hasLink(cell, link)),
      );
    } else {
      return widget.elements.some(element => BuilderTemplateService.hasLink(element, link));
    }
  }

  static hasLink(item: unknown, link: string): boolean {
    return PageReferenceUrl.itemReferencesPage(item, link);
  }

  static isPageReferenceInMenu(menu: MenuWidget, link: string): boolean {
    const isPageReferenceInMenuItems: (menuItems: MenuElement[]) => boolean = (menuItems: MenuElement[]): boolean => {
      if (menuItems) {
        return menuItems.some(
          menuItem => BuilderTemplateService.hasLink(menuItem, link) || isPageReferenceInMenuItems(menuItem.items),
        );
      }
      return false;
    };

    return menu.menuSections.some(section => isPageReferenceInMenuItems(section.items));
  }

  static getOffsetForMovingTheWidget(box: DOMRect): number {
    const elementIsBelowVisibleViewport = box.top > this.windowHeight;
    const elementIsNotFullyVisibleAtTheBottom = box.bottom > this.windowHeight;
    const elementIsSmallerThanTheViewport = box.height < this.windowHeight;
    const offsetForMovingTheElementToTheBottomSide = this.windowHeight - box.height - 100;
    const offsetForMovingTheElementToTheTopSide = 100;

    return (elementIsBelowVisibleViewport || elementIsNotFullyVisibleAtTheBottom) && elementIsSmallerThanTheViewport
      ? offsetForMovingTheElementToTheBottomSide
      : offsetForMovingTheElementToTheTopSide;
  }
}
